
import EChart from "@/features/model/components/graph/EChart.vue";
import Vue from "vue";
import XLSX from "xlsx";
import ButtonDanger from "@/components/ButtonDanger.vue"; // import ButtonReload from "@/components/ButtonReload.vue";
// import DateTimeRange from "@/components/DateTimeRange.vue";
import LoadingGraph from "@/components/LoadingGraph.vue";
import NoDataAvailable from "@/components/NoDataAvailable.vue";
import { mapActions } from "vuex";

export default Vue.extend({
  name: "McaCurrentSpectrum3D",
  components: { EChart, ButtonDanger, LoadingGraph, NoDataAvailable },
  data() {
    const fileName = [];
    const storeData: any = [];
    const additional: any = {
      rotor: [],
      eccentric: [],
      stator: [],
      statorISO: [],
    };
    const markPointDataList = [];
    const markPointDataListTransformData = [];

    return {
      source: null,
      sensorTypeData: ["IA", "IB", "IC"],
      frequencyRangeType: { name: "FFTIB (Low Frequency)", id: 19, equipmentModelTypeId: 2, groupCode: "FL" },
      selectFileNameMaxLength: 5,
      sensorType: "IA",
      hasMarkLineValue: [],
      chartResultData: [],
      noOfHarmonicLine: 3,
      noOfSidebandLine: 5,
      chartLoading: false,
      xAxisType: "Hz",
      fileName,
      isFileName: [],
      storeData,
      additional,
      markPointDataList,
      markPointDataListTransformData,
      firstPoint: undefined,
      secondaryPoint: undefined,
      diffPoint: undefined,
      chartOptions: {
        title: {
          text: "3D spectrum",
          left: "center",
        },
        grid3D: {
          boxWidth: 200,
          boxHeight: 80,
          viewControl: {
            projection: "perspective",
            alpha: 30,
            beta: 30,
            distance: 280,
          },
          splitLine: {
            show: true,
            lineStyle: {
              opacity: 0.5,
              type: "dotted",
            },
          },
        },
        tooltip: {
          trigger: "item",
          formatter: function (param: any) {
            const formatter = new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });

            return `
            <div style="min-width: 220px">
             <div class='flex items-center gap-x-[6px] text-[12px]'>
                <span style="background-color:${
                  param.color
                }; height:10px; width:10px; border-radius:50%; display:inline-block;"></span></span> <span >${
              param.value[1]
            }</span>
              </div>
              <div class='flex items-center gap-x-[6px] text-[12px]'>
                <span >Frequency (Hz): </span>
                <span >${formatter.format(param.value[0])}</span>
              </div>
              <div class='flex items-center gap-x-[6px] text-xs'>
                <span>Amplitude (dB): ${formatter.format(param.value[2])}</span>

              </div>
            </div>`;
          },
          position: function () {
            // fixed at top
            return ["75%", "0%"];
          },
        },
        legend: { data: [], top: 40 },
        xAxis3D: {
          name: "",
          type: "value",
          nameLocation: "middle",
          nameTextStyle: { fontSize: 13 },
        },
        yAxis3D: {
          name: "",
          type: "category",
          nameLocation: "middle",
          nameTextStyle: { fontSize: 13 },
        },
        zAxis3D: {
          name: "",
          type: "value",
          nameLocation: "middle",
          nameTextStyle: { fontSize: 13 },
        },
        series: [],
      },
    };
  },
  computed: {
    model() {
      return this.$store.getters.model;
    },
    isSecondaryTab() {
      return this.$store.state.graphInformationTab.is_secondary_tab.id;
    },
    frequencyRangeList() {
      return this.$store.state.graphInformationTabGroupData.graph_secondary_tab;
    },
    groupCode() {
      return this.$store.state.graphInformationTab.is_secondary_tab.groupCode;
    },
    equipmentCode() {
      return this.$store.state.model.equipmentCode.code;
    },
  },
  async created() {
    await this.fetchFileName();
  },
  methods: {
    ...mapActions(["setGraphInformationTabGroup", "getGraphInformationSecondaryTabGroupData"]),
    onChangeSecondaryTab(event: any) {
      const { id, name, key, groupCode } = event;

      // reset file name selected when change changeSecondaryTab
      this.isFileName = [];

      this.setGraphInformationTabGroup({
        ...this.$store.state.graphInformationTab,
        is_secondary_tab: { id, name, key, groupCode },
      });
    },

    async fetchResultData() {
      try {
        this.chartLoading = true;
        this.xAxisMin = undefined;
        this.xAxisMax = undefined;

        let storeData: Array<any> = [];
        storeData = await (this as any).$dep.modelUseCase.getEquipmentModelDataResultFrequenct(
          this.model.modelCode,
          this.isFileName,
          this.groupCode
        );

        this.storeData = storeData && (storeData as any).series;

        this.chartOptions.xAxis3D.name = "Frequency (Hz)";
        this.chartOptions.zAxis3D.name = "Amplitude (dB)";
        this.chartOptions.legend.data = (storeData as any).series.map((val: any) => val.alias);

        const chartColors = ["#5470C6", "#92CC76", "#FAC858", "#EF6666", "#8C2FE9"]; // [blue, green, yellow, red, purple]

        this.chartResultData = (storeData as any).series;

        this.chartOptions.series = (storeData as any).series
          .map((v, index) => this.series(v.data, index, v.alias, chartColors[index]))
          .flat();

        const dataWithLength = (storeData as any).series.filter((v) => v.data.length > 0);

        this.setChartMaxMin(dataWithLength);
      } catch (error) {
        console.log(error);
      } finally {
        this.chartLoading = false;
        this.source = null;
      }
    },
    onChangeSensorType(param: any) {
      const { id, name } = param;
      this.sensorType = { id, name };
    },

    series(datas: any, index: number, alias: string, chartColor: string) {
      const colorsPalette = [
        ["#1e40af", "#1d4ed8", "#2563eb", "#60a5fa", "#93c5fd"], // tw blue 800 700 600 500 400 300
        ["#166534", "#15803d", "#16a34a", "#4ade80", "#86efac"], // tw green  800 700 600 500 400 300
        ["#854d0e", "#a16207", "#ca8a04", "#facc15", "#fde047"], // tw yellow  800 700 600 500 400 300
        ["#991b1b", "#b91c1c", "#dc2626", "#f87171", "#fca5a5"], // tw red  800 700 600 500 400 300
        ["#6b21a8", "#7e22ce", "#9333ea", "#c084fc", "#d8b4fe"], // tw purple  800 700 600 500 400 300
      ];

      const checkSensorType = (val: any) => {
        switch (this.sensorType) {
          case "IA":
            return { type: alias, data: val.map((val) => [val.FHZ, alias, val.IA]) };
          case "IB":
            return { type: alias, data: val.map((val) => [val.FHZ, alias, val.IB]) };
          case "IC":
            return { type: alias, data: val.map((val) => [val.FHZ, alias, val.IC]) };
        }
      };

      const checkStatus = (name: string, val: any, sensorType: string, alias: string) => {
        const rotor = [];
        const eccentric = [];
        const stator = [];
        const statorISO = [];
        const speed = [];

        val.forEach((valCheck) => {
          const point = [valCheck.FHZ, alias, valCheck[`${sensorType}`]]; // Use [x, z, y] for 3D
          if (valCheck[`${name}`] === 1 && valCheck[`${sensorType}_AMP`]) rotor.push(point);
          if (valCheck[`${name}`] === 2 && valCheck[`${sensorType}_AMP`]) eccentric.push(point);
          if (valCheck[`${name}`] === 3 && valCheck[`${sensorType}_AMP`]) stator.push(point);
          if (valCheck[`${name}`] === 4 && valCheck[`${sensorType}_AMP`]) statorISO.push(point);
          if (valCheck[`${name}`] === 8 && valCheck[`${sensorType}_AMP`]) speed.push(point);
        });
        return [rotor, eccentric, stator, statorISO, speed];
      };

      const markPoint = (val: any, alias: string) => {
        let rotor = [],
          eccentric = [],
          stator = [],
          statorISO = [],
          speed = [];

        switch (this.sensorType) {
          case "IA":
            [rotor, eccentric, stator, statorISO, speed] = checkStatus("IA_CHECK", val, "IA", alias);
            break;
          case "IB":
            [rotor, eccentric, stator, statorISO, speed] = checkStatus("IB_CHECK", val, "IB", alias);
            break;
          case "IC":
            [rotor, eccentric, stator, statorISO, speed] = checkStatus("IC_CHECK", val, "IC", alias);
            break;
        }

        // Scatter series for points
        return [
          {
            name: alias,
            type: "scatter3D",
            data: rotor,
            symbolSize: 10,
            itemStyle: { color: colorsPalette[index][0] },

            labelName: "Rotor Crack",
          },
          {
            name: alias,
            type: "scatter3D",
            data: eccentric,
            symbolSize: 10,
            itemStyle: { color: colorsPalette[index][1] },
            labelName: "Eccentric Air Gap",
          },
          {
            name: alias,
            type: "scatter3D",
            data: stator,
            symbolSize: 10,
            itemStyle: { color: colorsPalette[index][2] },
            labelName: "Mechanical Failure",
          },
          {
            name: alias,
            type: "scatter3D",
            data: statorISO,
            symbolSize: 10,
            itemStyle: { color: colorsPalette[index][3] },
            labelName: "Stator Short Turn",
          },
          {
            name: alias,
            type: "scatter3D",
            data: speed,
            symbolSize: 10,
            itemStyle: { color: colorsPalette[index][4] },
            labelName: "Speed",
          },
        ];
      };

      const pointAdd = markPoint(datas, alias); // Get mark points
      const dataSensor = checkSensorType(datas); // Get the sensor data

      this.markPointDataList = [...this.markPointDataList, ...pointAdd];

      this.markPointDataListTransformData = Object.values(
        [...this.markPointDataList]
          .filter((v) => v.data.length > 0)
          .reduce((acc, item) => {
            if (!acc[item.name]) {
              acc[item.name] = [];
            }
            acc[item.name].push(item);
            return acc;
          }, {})
      );

      const sensorSeries = {
        name: dataSensor.type,
        type: "line3D",
        data: dataSensor.data,
        lineStyle: {
          width: 2,
          color: chartColor,
        },
      };

      // Combine sensor series and marker series
      const combinedSeries = [sensorSeries, ...pointAdd];

      return combinedSeries;
    },

    exportExcel() {
      const { id } = this.model;
      const name = `mca_${id}__current_spectrum`.toUpperCase();
      // export by 1 chart
      const ws = XLSX.utils.json_to_sheet(this.chartResultData[0].data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, name); // => sheet name
      XLSX.writeFile(wb, `${name}.xlsx`); // => file name
    },
    async onChangeFileName(values: any) {
      this.isFileName = values.map((val: any) => ({
        fileName: val.fileName,
        alias: val.alias,
      }));
    },
    async fetchFileName() {
      try {
        (this as any).fileName = await (this as any).$dep.modelUseCase.getFileNameMca(this.model.modelCode, this.groupCode);
        if (this.fileName.length === 0) {
          this.chartLoading = false;
          this.chartResultData = [];
          throw "No Datas";
        }
      } catch (error) {
        if (!(this as any).isFileName) {
          this.chartLoading = false;
          return;
        }
      }
    },

    async onSubmit() {
      this.graphLoading = true;
      this.markPointDataList = [];
      await this.fetchResultData();
      this.graphLoading = false;

      //   Clear markPoint
      this.clearMarkPoint();
    },

    limiter(val: any[]) {
      if (val.length > this.selectFileNameMaxLength) val.shift();
    },
    clearMarkPoint() {
      this.firstPoint = undefined;
      this.secondaryPoint = undefined;
      this.diffPoint = undefined;
      this.hasMarkLineValue = [];
      this.chartOptions.series = this.chartOptions.series.filter((item) => item.name !== "markLine");
      this.updateChart(this.chartOptions, { notMerge: true });
    },
    handleChartClick(params) {
      const value = params.value;
      const markLineColor = "red";
      const markPointLabelColor = "#333";

      // For plotLine harmonic and sideband if type === "DM" use 0, for others use -150
      const yaXisClickLineNo = this.frequencyRangeType.groupCode === "DM" ? 0 : -150;

      if (value[0] < this.firstPoint) {
        this.$swal({
          icon: "warning",
          title: "Warning",
          text: "Couldn't select 2nd less than ref peak",
        });
        return;
      }
      if (
        params.seriesType === "line3D" &&
        (typeof this.firstPoint === "undefined" || typeof this.secondaryPoint === "undefined") &&
        (typeof this.firstPoint === "undefined" || value[0] > this.firstPoint)
      ) {
        const isFirstClick = typeof this.firstPoint === "undefined";

        const data = this.chartOptions.series
          .filter((val) => val.name && val.name !== "markLine")
          .map((val) => [value[0], val.name, yaXisClickLineNo]);

        if (isFirstClick) {
          this.firstPoint = value[0];
        } else {
          this.secondaryPoint = value[0];
        }

        // show only first and last point
        const filteredData = data.length > 1 ? [data[0], data[data.length - 1]] : data;

        const newMarkLine = [
          {
            type: "line3D",
            name: "markLine",
            data,
            lineStyle: {
              color: markLineColor,
              type: "dotted",
            },
          },
          {
            type: "scatter3D",
            name: "markLine",
            data: filteredData,
            symbolSize: 4,
            itemStyle: {
              color: markLineColor,
            },
            label: {
              show: true,
              formatter: (params) => `${params.value[0]}`, // Show value on the scatter point
              position: "top", // Adjust as necessary
              textStyle: {
                color: markPointLabelColor,
              },
            },
          },
        ];
        // Add the new markLine to the chart options
        this.chartOptions.series = this.chartOptions.series.concat(newMarkLine);

        // Update the chart
        this.updateChart(this.chartOptions);
      }
    },
    harmonicHandle() {
      const markLineColor = "red";
      const markPointLabelColor = "#333";
      // For plotLine harmonic and sideband if type === "DM" use 0, for others use -150
      const yaXisClickLineNo = this.frequencyRangeType.groupCode === "DM" ? 0 : -150;
      if (this.noOfHarmonicLine < 1 || this.noOfHarmonicLine > 100) {
        this.$swal({
          icon: "warning",
          title: "Harmonic value out of range",
          text: "Please select a value between 1-100 to display.",
        });
        return;
      }

      if (this.storeData.length < 2) {
        this.$swal({
          icon: "warning",
          title: "Unable to display harmonic",
          text: "Please select more than one chart to display the harmonic line.",
        });
        return;
      }

      if (this.firstPoint && this.secondaryPoint) {
        this.diffPoint = this.secondaryPoint - this.firstPoint;

        const yAxisValue = this.chartOptions.series
          .filter((val) => val.name && val.name !== "markLine")
          .map((val) => val.name);

        let arr = [];
        for (var i = 0; i < this.noOfHarmonicLine; i++) {
          const diff = this.firstPoint + (i + 2) * this.diffPoint;

          const yAxisData = yAxisValue.map((val) => [diff, val, yaXisClickLineNo]);
          const filteredData = yAxisData.length > 1 ? [yAxisData[0], yAxisData[yAxisData.length - 1]] : yAxisData;

          if (diff < this.maxXAxisValue) {
            // Create a new line3D for each harmonic
            arr.push(
              {
                type: "line3D",
                name: "markLine",
                data: yAxisValue.map((val) => [diff, val, yaXisClickLineNo]),
                lineStyle: {
                  color: markLineColor,
                  type: "dotted",
                },
                label: {
                  show: true,
                  formatter: () => `Harmonic ${i + 1}: ${diff.toFixed(2)}`, // Label for harmonic line
                  position: "middle",
                  textStyle: {
                    color: markPointLabelColor,
                  },
                },
              },
              {
                type: "scatter3D",
                name: "markLine",
                data: filteredData,
                symbolSize: 4,
                itemStyle: {
                  color: markLineColor,
                },
                label: {
                  show: true,
                  formatter: () => `${diff.toFixed(2)}`,
                  position: "middle",
                  textStyle: {
                    color: markPointLabelColor,
                  },
                },
              }
            );
          }
        }

        this.hasMarkLineValue = arr;

        // Add the new markLines to the chart options
        this.chartOptions.series = this.chartOptions.series.concat(arr);
        // Update the chart
        this.updateChart(this.chartOptions);
      }
    },
    sidebandHandle() {
      const markLineColor = "red";
      const markPointLabelColor = "#333";
      // For plotLine harmonic and sideband if type === "DM" use 0, for others use -150
      const yaXisClickLineNo = this.frequencyRangeType.groupCode === "DM" ? 0 : -150;

      if (this.noOfSidebandLine < 1 || this.noOfSidebandLine > 100) {
        this.$swal({
          icon: "warning",
          title: "Sideband value out of range",
          text: "Please select a value between 1-100 to display.",
        });
        return;
      }
      if (this.storeData.length < 2) {
        this.$swal({
          icon: "warning",
          title: "Unable to display sideband",
          text: "Please select more than one chart to display the sideband line.",
        });
        return;
      }
      if (this.firstPoint && this.secondaryPoint) {
        this.diffPoint = this.secondaryPoint - this.firstPoint;
        const yAxisValue = this.chartOptions.series
          .filter((val) => val.name && val.name !== "markLine")
          .map((val) => val.name);
        let arr = [];

        // right
        for (let i = 1; i <= this.noOfSidebandLine - 1; i++) {
          const diff = this.secondaryPoint + i * this.diffPoint;
          const yAxisData = yAxisValue.map((val) => [diff, val, yaXisClickLineNo]);
          const filteredData = yAxisData.length > 1 ? [yAxisData[0], yAxisData[yAxisData.length - 1]] : yAxisData;
          if (diff < this.maxXAxisValue) {
            arr.push(
              {
                type: "line3D",
                name: "markLine",
                data: yAxisValue.map((val) => [diff, val, yaXisClickLineNo]),
                lineStyle: {
                  color: markLineColor,
                  type: "dotted",
                },
              },
              {
                type: "scatter3D",
                name: "markLine",
                data: filteredData,
                symbolSize: 4,
                itemStyle: {
                  color: markLineColor,
                },
                label: {
                  show: true,
                  formatter: () => `${diff.toFixed(2)}`,
                  position: "middle",
                  textStyle: {
                    color: markPointLabelColor,
                  },
                },
              }
            );
          }
        }

        // left
        for (let i = 1; i <= this.noOfSidebandLine; i++) {
          const diff = this.firstPoint - i * this.diffPoint;
          const yAxisData = yAxisValue.map((val) => [diff, val, yaXisClickLineNo]);
          const filteredData = yAxisData.length > 1 ? [yAxisData[0], yAxisData[yAxisData.length - 1]] : yAxisData;
          if (diff > this.minXAxisValue) {
            arr.push(
              {
                type: "line3D",
                name: "markLine",
                data: yAxisValue.map((val) => [diff, val, yaXisClickLineNo]),
                lineStyle: {
                  color: markLineColor,
                  type: "dotted",
                },
              },
              {
                type: "scatter3D",
                name: "markLine",
                data: filteredData,
                symbolSize: 4,
                itemStyle: {
                  color: markLineColor,
                },
                label: {
                  show: true,
                  formatter: () => `${diff.toFixed(2)}`,
                  position: "middle",
                  textStyle: {
                    color: markPointLabelColor,
                  },
                },
              }
            );
          }
        }

        this.hasMarkLineValue = arr;
        // Add the new markLine to the chart options
        this.chartOptions.series = this.chartOptions.series.concat(arr);
        // Update the chart
        this.updateChart(this.chartOptions);
      }
    },
    updateChart(options, opts) {
      this.$root.McaCurrentSpectrum3DChartRef.setOption(options, opts);
    },
    setChartMaxMin(dataWithLength) {
      if (!dataWithLength.length) {
        this.maxXAxisValue = undefined;
        this.minXAxisValue = undefined;
        return;
      }
      const { data } = dataWithLength[0];
      this.maxXAxisValue = data[data.length - 1].FHZ;
      this.minXAxisValue = data[0].FHZ;
    },
  },
  watch: {
    async isSecondaryTab() {
      await this.fetchFileName();
    },
  },
});
